const getLabelKey = (label) => String(label || "").trim().toLowerCase();

const normalizeFieldName = (fieldName = "") =>
  String(fieldName || "")
    .toLowerCase()
    .replace(/[^a-z0-9]/g, "");

const LABEL_NAME_KEYS = ["name", "Name", "label_name", "labelName", "Label Name"];

const matchesSchemaConst = (value, schemaConst) => {
  if (schemaConst === undefined) return false;
  if (value === schemaConst) return true;

  if (typeof schemaConst === "boolean") {
    return value === String(schemaConst) || value === (schemaConst ? "Yes" : "No");
  }

  return false;
};

export const splitLabelNames = (labelsValue) =>
  String(labelsValue || "")
    .split(/,+/g)
    .map((label) => label.trim())
    .filter(Boolean);

const getLabelName = (label, nameKey) => {
  if (typeof label === "string") return label;
  if (!label || typeof label !== "object") return "";

  if (nameKey && label[nameKey] !== undefined) {
    return label[nameKey];
  }

  const fallbackKey = LABEL_NAME_KEYS.find((key) => label[key] !== undefined);
  return fallbackKey ? label[fallbackKey] : "";
};

export const getDuplicateLabelEntries = (labels = [], nameKey) => {
  const seen = new Map();
  const duplicatesByKey = new Map();

  labels.forEach((label, index) => {
    const labelName = String(getLabelName(label, nameKey) || "").trim();
    const labelKey = getLabelKey(labelName);

    if (!labelKey) return;

    if (!seen.has(labelKey)) {
      seen.set(labelKey, { index, name: labelName });
      return;
    }

    const firstSeen = seen.get(labelKey);
    const duplicateEntry = duplicatesByKey.get(labelKey) || {
      name: firstSeen.name || labelName,
      indices: [firstSeen.index],
    };

    duplicateEntry.indices.push(index);
    duplicatesByKey.set(labelKey, duplicateEntry);
  });

  return Array.from(duplicatesByKey.values());
};

export const getDuplicateLabelNames = (labelNames = []) =>
  getDuplicateLabelEntries(labelNames).map((duplicate) => duplicate.name);

export const getDetailedLabelNames = (labels = []) =>
  Array.isArray(labels)
    ? labels.map((label) => getLabelName(label, "name"))
    : [];

export const formatDuplicateLabelsError = (duplicates = []) =>
  `Duplicate label names are not allowed: ${duplicates.join(", ")}.`;

const getLabelNamePropertyKey = (schemaNode = {}) => {
  const properties = schemaNode?.items?.properties || {};
  return LABEL_NAME_KEYS.find((key) => properties[key]);
};

const isLabelNameArraySchema = (fieldKey = "", schemaNode = {}, options = {}) => {
  if (schemaNode?.type !== "array" || !getLabelNamePropertyKey(schemaNode)) {
    return false;
  }

  const namesToCheck = [fieldKey, schemaNode?.title]
    .map(normalizeFieldName)
    .filter(Boolean);

  if (
    !options.includeDetailedLabels &&
    namesToCheck.some((name) => name === "detailedlabels")
  ) {
    return false;
  }

  return namesToCheck.some((name) =>
    name === "labels" ||
    name === "label" ||
    name === "labelspromptkey" ||
    name === "labelpromptkey" ||
    name === "labelsprompt" ||
    name.includes("labels")
  );
};

const addDuplicateErrors = (errorsNode, duplicates = [], nameKey = "name") => {
  if (!errorsNode || duplicates.length === 0) return;

  const duplicateNames = duplicates.map((duplicate) => duplicate.name);
  const duplicateMessage = formatDuplicateLabelsError(duplicateNames);

  errorsNode.addError?.(duplicateMessage);

  duplicates.forEach((duplicate) => {
    duplicate.indices.forEach((index) => {
      const itemErrors = errorsNode?.[index];
      const nameErrors = itemErrors?.[nameKey];

      if (nameErrors?.addError) {
        nameErrors.addError("Duplicate label name.");
        return;
      }

      itemErrors?.addError?.("Duplicate label name.");
    });
  });
};

export const validateDuplicateLabelsInSchema = (
  schemaNode,
  dataNode,
  errorsNode,
  fieldKey = "",
  options = {}
) => {
  if (!schemaNode || typeof schemaNode !== "object" || !errorsNode) return;

  if (
    isLabelNameArraySchema(fieldKey, schemaNode, options) &&
    Array.isArray(dataNode)
  ) {
    const nameKey = getLabelNamePropertyKey(schemaNode);
    const duplicateEntries = getDuplicateLabelEntries(dataNode, nameKey);
    addDuplicateErrors(errorsNode, duplicateEntries, nameKey);
  }

  if (schemaNode.type === "array") {
    if (Array.isArray(dataNode) && schemaNode.items) {
      dataNode.forEach((item, index) => {
        validateDuplicateLabelsInSchema(
          schemaNode.items,
          item,
          errorsNode?.[index],
          fieldKey,
          options
        );
      });
    }

    return;
  }

  Object.entries(schemaNode.properties || {}).forEach(([childKey, childSchema]) => {
    validateDuplicateLabelsInSchema(
      childSchema,
      dataNode?.[childKey],
      errorsNode?.[childKey],
      childKey,
      options
    );
  });

  Object.entries(schemaNode.dependencies || {}).forEach(([dependencyKey, dependency]) => {
    if (Array.isArray(dependency?.oneOf)) {
      const dependencyValue = dataNode?.[dependencyKey];
      const selectedBranch = dependency.oneOf.find((branch) =>
        matchesSchemaConst(
          dependencyValue,
          branch?.properties?.[dependencyKey]?.const
        )
      );

      if (selectedBranch) {
        validateDuplicateLabelsInSchema(
          selectedBranch,
          dataNode,
          errorsNode,
          dependencyKey,
          options
        );
      }

      return;
    }

    validateDuplicateLabelsInSchema(
      dependency,
      dataNode,
      errorsNode,
      dependencyKey,
      options
    );
  });
};

export const getDuplicateLabelNamesInSchema = (
  schemaNode,
  dataNode,
  fieldKey = "",
  options = {}
) => {
  const duplicateNames = [];
  const duplicateKeys = new Set();

  const addDuplicateNames = (names = []) => {
    names.forEach((name) => {
      const trimmedName = String(name || "").trim();
      const labelKey = getLabelKey(trimmedName);

      if (!labelKey || duplicateKeys.has(labelKey)) return;

      duplicateKeys.add(labelKey);
      duplicateNames.push(trimmedName);
    });
  };

  const walk = (currentSchema, currentData, currentFieldKey = "") => {
    if (!currentSchema || typeof currentSchema !== "object") return;

    if (isLabelNameArraySchema(currentFieldKey, currentSchema, options)) {
      const labelsSource = Array.isArray(currentData)
        ? currentData
        : currentSchema.default;

      if (Array.isArray(labelsSource)) {
        const nameKey = getLabelNamePropertyKey(currentSchema);
        addDuplicateNames(
          getDuplicateLabelEntries(labelsSource, nameKey).map(
            (duplicate) => duplicate.name
          )
        );
      }
    }

    if (currentSchema.type === "array") {
      if (Array.isArray(currentData) && currentSchema.items) {
        currentData.forEach((item) => {
          walk(currentSchema.items, item, currentFieldKey);
        });
      }

      return;
    }

    Object.entries(currentSchema.properties || {}).forEach(
      ([childKey, childSchema]) => {
        walk(childSchema, currentData?.[childKey], childKey);
      }
    );

    Object.entries(currentSchema.dependencies || {}).forEach(
      ([dependencyKey, dependency]) => {
        if (Array.isArray(dependency?.oneOf)) {
          const dependencyValue = currentData?.[dependencyKey];
          const selectedBranch = dependency.oneOf.find((branch) =>
            matchesSchemaConst(
              dependencyValue,
              branch?.properties?.[dependencyKey]?.const
            )
          );

          if (selectedBranch) {
            walk(selectedBranch, currentData, dependencyKey);
          }

          return;
        }

        walk(dependency, currentData, dependencyKey);
      }
    );
  };

  walk(schemaNode, dataNode, fieldKey);

  return duplicateNames;
};
